import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { setLanguage, setTheme } from '../../../Redux/uiSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { routes } from '../../../Utils/routes';
import './NavDrawer.css';

interface Item { to: string; label: TranslationKey; icon: string }

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Every screen the desktop menu offers. The bottom tab bar only has room for
 * five destinations (see Menu.tsx), so anything past those - Lessons,
 * Profile, Backup, Premium included - had no way in on a phone before this
 * existed.
 */
const MAIN_ITEMS: readonly Item[] = [
  { to: routes.dashboard, label: 'navDashboard', icon: 'M4 20h16M7 20V10m5 10V4m5 16v-7' },
  { to: routes.calendar, label: 'navCalendar', icon: 'M7 3v2m10-2v2M4 8h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z' },
  { to: routes.payments, label: 'navPayments', icon: 'M6 3h12v18l-3-2-3 2-3-2-3 2V3zm3 5h6M9 12h6M9 16h4' },
  { to: routes.students, label: 'navStudents', icon: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8 1a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM3 20c.6-3.2 3.1-5 6-5s5.4 1.8 6 5m-.5 0c.4-2.4 2.1-3.8 4.3-3.8 1.3 0 2.4.5 3.2 1.4' },
  { to: routes.ensembles, label: 'navEnsembles', icon: 'M9 18V5l10-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm10-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z' },
  { to: routes.lessons, label: 'navLessons', icon: 'M4 6h16M4 12h16M4 18h10' },
  { to: routes.profile, label: 'navProfile', icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8.4-3a8.4 8.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a8.3 8.3 0 0 0-2-1.2L15.5 3h-4l-.4 2.6a8.3 8.3 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a8.4 8.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1c.6.5 1.3.9 2 1.2l.4 2.6h4l.4-2.6c.7-.3 1.4-.7 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z' },
  { to: routes.backup, label: 'navBackup', icon: 'M6 18a4 4 0 0 1-1-7.9A6 6 0 0 1 17 9a4 4 0 0 1 1 7.9M12 12v8m0 0-3-3m3 3 3-3' },
  { to: routes.premiumArea, label: 'navPremium', icon: 'm12 3 2.5 5.5L20 9l-4 4 1 5.7L12 16l-5 2.7L8 13 4 9l5.5-.5L12 3z' }
];

/**
 * The premium screens, same set as PremiumSubnav, so a phone can reach them
 * directly instead of only after already being on a premium page.
 */
const PREMIUM_ITEMS: readonly { to: string; label: TranslationKey }[] = [
  { to: routes.receipts, label: 'navReceipts' },
  { to: routes.receiptReports, label: 'navReceiptReports' },
  { to: routes.business, label: 'businessTitle' },
  { to: routes.openFormat, label: 'openFormatTitle' },
  { to: routes.reports, label: 'navReports' },
  { to: routes.reminders, label: 'navReminders' },
  { to: routes.paymentDocuments, label: 'navPaymentDocuments' }
];

// Line-art only: this app does not use emoji for controls. A circle with
// eight rays and a crescent, the two glyphs every icon set draws for
// light/dark, in the same single-path shape the rest of this file's icons use.
const SUN_ICON = 'M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0zM12 1v2M12 21v2M4.22 4.22l1.42 1.42'
  + 'M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42';
const MOON_ICON = 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z';
const GLOBE_ICON = 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM2 12h20'
  + 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z';

function NavIcon({ path }: { path: string }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d={path} /></svg>;
}

/**
 * The one side menu for the whole app, on a phone and on a desktop alike:
 * every destination the desktop menu offers, the premium screens
 * PremiumSubnav lists, and now language and theme too, so neither has to sit
 * pinned in the header on every screen. Reachable from anywhere rather than,
 * for the premium screens, only once already on /user/premium.
 *
 * Behaves like Modal: Escape closes it, a backdrop click closes it, Tab
 * cycles within it while open, and focus returns to whatever opened it on
 * close - a keyboard user is never dropped at the top of the document.
 */
export function NavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useAppSelector(state => state.ui.theme);
  const isAdmin = useAppSelector(state => state.session.subscription?.isAdmin ?? false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    drawerRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const premiumItems = isAdmin
    ? [...PREMIUM_ITEMS, { to: routes.admin, label: 'navAdmin' as TranslationKey }]
    : PREMIUM_ITEMS;

  return (
    <div
      className="nav-drawer-overlay"
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}
    >
      <nav className="nav-drawer" aria-label={tr('navDashboard')} aria-modal="true" role="dialog" ref={drawerRef}>
        <div className="nav-drawer-header">
          <span className="nav-drawer-title">{tr('appName')}</span>
          <button type="button" className="nav-drawer-close" onClick={onClose} aria-label={tr('close')}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <ul className="nav-drawer-list">
          {MAIN_ITEMS.map(item => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === routes.dashboard}
                onClick={onClose}
                className={({ isActive }) => isActive ? 'nav-drawer-item is-active' : 'nav-drawer-item'}
              >
                <NavIcon path={item.icon} />
                <span>{tr(item.label)}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <p className="nav-drawer-section-title">{tr('navPremium')}</p>
        <ul className="nav-drawer-list nav-drawer-list-plain">
          {premiumItems.map(item => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onClose}
                className={({ isActive }) => isActive ? 'nav-drawer-item is-active' : 'nav-drawer-item'}
              >
                <span>{tr(item.label)}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <p className="nav-drawer-section-title">{tr('displayTitle')}</p>
        <ul className="nav-drawer-list nav-drawer-list-plain">
          {/* Left open rather than closing the drawer: the point is to see
              the change land before deciding whether to look at anything else. */}
          <li>
            <button
              type="button"
              className="nav-drawer-item nav-drawer-action"
              onClick={() => dispatch(setLanguage(language === 'he' ? 'en' : 'he'))}
            >
              <NavIcon path={GLOBE_ICON} />
              <span>{tr('languageLabel')}</span>
              <span className="nav-drawer-action-value">{language === 'he' ? 'English' : 'עברית'}</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              className="nav-drawer-item nav-drawer-action"
              onClick={() => dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'))}
            >
              <NavIcon path={theme === 'dark' ? SUN_ICON : MOON_ICON} />
              <span>{tr('themeLabel')}</span>
              <span className="nav-drawer-action-value">{tr(theme === 'dark' ? 'themeLight' : 'themeDark')}</span>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
